import { RootState, useAppDispatch } from '@AppState/redux-store'
import {
  AuthScopes,
  ModuleWithScopes,
  Role,
  Scope,
  UpsertRoleRequest,
} from '@Generated/back-office-client/src'
import { Button, CheckboxCards, Flex, Text, TextField } from '@radix-ui/themes'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { flatten, intersectionBy } from 'lodash'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { DEFAULT_STALE_DATA_TIMEOUT } from '@AppState/app-config'
import { UsersServiceAPI } from '@API/data-services/users-service'
import { Routes } from '@Components/main-app/types'
import { Accordion, AccordionItems } from '@Components/common/accordion'
import { Accordion as RadixAccordion, Form } from 'radix-ui'

export const ModulePermissionsContent = ({
  scopes,
  selectedScopes,
  onScopesSelected,
}: {
  scopes: Scope[]
  selectedScopes: string[]
  onScopesSelected: (scopes: AuthScopes[]) => void
}) => {
  const scopeItems = scopes.map((scope) => {
    return (
      <CheckboxCards.Item key={scope.id} value={scope.name}>
        <Flex direction="column">
          <Text weight="bold">{scope.name}</Text>
          <Text>{scope.description}</Text>
        </Flex>
      </CheckboxCards.Item>
    )
  })

  return (
    <RadixAccordion.Content className="AccordionContent">
      <CheckboxCards.Root
        columns={{ initial: '1', sm: '3' }}
        defaultValue={selectedScopes}
        onValueChange={(scopes) => onScopesSelected(scopes as AuthScopes[])}>
        {scopeItems}
      </CheckboxCards.Root>
    </RadixAccordion.Content>
  )
}

export const ModulePermissions = ({
  moduleWithScopes,
  selectedScopes,
  onScopesSelected,
}: {
  selectedScopes: Record<string, string[]> | null
  moduleWithScopes: ModuleWithScopes[]
  onScopesSelected: (moduleId: string, scopeIds: AuthScopes[]) => void
}) => {
  const accordianItems = moduleWithScopes.reduce((acc, module) => {
    const child = (
      <ModulePermissionsContent
        selectedScopes={selectedScopes ? selectedScopes[module.id] : []}
        scopes={module.scopes}
        onScopesSelected={(scopeIds: AuthScopes[]) => onScopesSelected(module.id, scopeIds)}
      />
    )

    const item = { id: module.id, value: module.name, name: module.name, child }
    acc.push(item)

    return acc
  }, [] as AccordionItems[])

  return <Accordion items={accordianItems} />
}

export const UpsertRole = () => {
  const dispatch = useAppDispatch()
  const { activeCustomerId } = useSelector((state: RootState) => state.users)
  const location = useLocation()
  const nav = useNavigate()
  const params = useParams()
  const [selectedScopes, setScopesSelected] = useState<Record<string, AuthScopes[]> | null>(null)
  const [scopeError, setScopeError] = useState<boolean>(
    !!(selectedScopes && Object.values(selectedScopes).every((s) => !s.length)),
  )

  const editRoleId = params?.roleId
  const roleForEdit = editRoleId ? (location.state as Role) : null

  const queryClient = useQueryClient()

  const { data: moduleScopes } = useQuery({
    queryKey: [`${activeCustomerId}-module-scopes`],
    staleTime: DEFAULT_STALE_DATA_TIMEOUT,
    queryFn: () => UsersServiceAPI.getCustomerScopes(),
  })

  const { mutate: upsertRole, isSuccess } = useMutation({
    mutationFn: (upsertRoleRequest: UpsertRoleRequest) => {
      return editRoleId
        ? UsersServiceAPI.updateRole(editRoleId, upsertRoleRequest)
        : UsersServiceAPI.createRole(upsertRoleRequest)
    },
  })

  useEffect(() => {
    // If we don't have the data in state for the role we're trying to load, go back to the roles list
    if (editRoleId && !roleForEdit) {
      nav(Routes.ROLES)
    } else if (roleForEdit && moduleScopes) {
      // Otherwise init the form with the existing role we're editing
      const defaultSelectedScopes: Record<string, AuthScopes[]> = {}
      for (const module of moduleScopes) {
        const scopeIntersection = intersectionBy(module.scopes, roleForEdit.scopes, 'id')

        defaultSelectedScopes[module.id] = scopeIntersection.map((s) => s.name)
      }
      setScopesSelected(defaultSelectedScopes)
    }
  }, [editRoleId, moduleScopes, nav, roleForEdit])

  useEffect(() => {
    if (isSuccess) {
      queryClient.invalidateQueries({ queryKey: [`${activeCustomerId}-roles`] })
      queryClient.invalidateQueries({ queryKey: [`${activeCustomerId}-extra-user-data`] })
      nav(Routes.ROLES)
    }
  }, [dispatch, nav, isSuccess, queryClient, activeCustomerId])

  return (
    <Form.Root
      onSubmit={(e) => {
        e.preventDefault()
        const formData = new FormData(e.currentTarget).entries()
        const data = Object.fromEntries(formData)

        const allScopes = flatten(selectedScopes ? Object.values(selectedScopes) : [])

        if (!selectedScopes || !allScopes.length) {
          setScopeError(true)
          return
        }

        const upsertRoleRequest: UpsertRoleRequest = {
          name: data.name as string,
          description: data.description as string,
          scopes: Array.from(new Set(allScopes)),
        }

        upsertRole(upsertRoleRequest)
      }}>
      <Flex direction="column" gap="4">
        <Form.Field name="name">
          <Flex justify="between">
            <Form.Label>Name</Form.Label>
            <Form.Message className="FormMessage" match="valueMissing">
              Name is required
            </Form.Message>
          </Flex>

          <Form.Control asChild required>
            <TextField.Root defaultValue={roleForEdit?.name} />
          </Form.Control>
        </Form.Field>

        <Form.Field name="description">
          <Flex justify="between">
            <Form.Label>Description</Form.Label>
            <Form.Message className="FormMessage" match="valueMissing">
              Description is required
            </Form.Message>
          </Flex>

          <Form.Control asChild required>
            <TextField.Root defaultValue={roleForEdit?.description} />
          </Form.Control>
        </Form.Field>

        <Form.Field name="roles">
          <Flex justify="between">
            <Form.Label>Module Permissions</Form.Label>
            {scopeError ? (
              <Form.Message className="FormMessage">
                At least one permission is required
              </Form.Message>
            ) : null}
          </Flex>

          {moduleScopes ? (
            <ModulePermissions
              selectedScopes={selectedScopes}
              moduleWithScopes={moduleScopes}
              onScopesSelected={(moduleId, checkedScopes) => {
                const newState = { ...selectedScopes }
                newState[moduleId] = checkedScopes
                setScopesSelected(newState)
              }}
            />
          ) : null}
        </Form.Field>

        <Form.Submit asChild>
          <Button style={{ cursor: 'pointer' }}>Submit</Button>
        </Form.Submit>
      </Flex>
    </Form.Root>
  )
}
